import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { cn } from '@/utils/cn';
import styles from '@/components/atoms/Toast/Toast.module.scss';

export interface ToastOptions {
  title?: string;
  description: string;
  duration?: number;
  variant?: 'default' | 'motivation' | 'success';
}

interface ToastItem extends ToastOptions {
  id: string;
  open: boolean;
}

interface ToastContextType {
  toast: (options: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

/**
 * ToastProvider - provides toast notifications globally.
 * Uses Radix UI Toast primitives with design system styling.
 */
export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, open: false } : t))
    );
    // Remove from DOM after animation
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 300);
  }, []);

  const toast = useCallback(
    (options: ToastOptions) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const duration = options.duration ?? 5000;

      setToasts((prev) => [
        ...prev,
        {
          ...options,
          id,
          open: true,
        },
      ]);

      if (duration > 0) {
        setTimeout(() => dismissToast(id), duration);
      }
    },
    [dismissToast]
  );

  return (
    <ToastContext.Provider value={{ toast }}>
      <ToastPrimitive.Provider duration={5000} label="Notification">
        {children}
        <ToastPrimitive.Viewport
          className={styles.toast__viewport}
          label="Notifications (F8)"
        />
        {toasts.map((item) => (
          <ToastPrimitive.Root
            key={item.id}
            open={item.open}
            onOpenChange={(open) => {
              if (!open) dismissToast(item.id);
            }}
            duration={item.duration ?? 5000}
            className={cn(
              styles.toast,
              item.variant === 'motivation' && styles['toast--motivation'],
              item.variant === 'success' && styles['toast--success']
            )}
          >
            <div className={styles.toast__content}>
              {item.title && (
                <ToastPrimitive.Title className={styles.toast__title}>
                  {item.title}
                </ToastPrimitive.Title>
              )}
              <ToastPrimitive.Description className={styles.toast__description}>
                {item.description}
              </ToastPrimitive.Description>
            </div>
          </ToastPrimitive.Root>
        ))}
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextType {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
